import type { Participant } from '#src/pages/create/types.ts';
import { Alert } from '@packages/ui/base/alert/alert';
import { Button } from '@packages/ui/base/button/button';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from '@packages/ui/base/input_group/input_group';
import { Tooltip } from '@packages/ui/base/tooltip/tooltip';
import { ArrowRightIcon, PlusIcon, XIcon } from '@phosphor-icons/react';
import type { ChangeEvent, KeyboardEvent } from 'react';
import { useEffect, useRef } from 'react';

export interface AddParticipantsStepProps {
  participants: Participant[];
  focusParticipantId: string | undefined;
  error: string | undefined;
  duplicateParticipantIds: Set<string>;
  onAppendParticipant: () => void;
  onInsertParticipant: (index: number) => void;
  onDeleteParticipant: (id: string) => void;
  onUpdateParticipantName: (id: string, name: string) => void;
  onNext: () => void;
}

export const AddParticipantsStep = ({
  participants,
  error,
  duplicateParticipantIds,
  focusParticipantId,
  onAppendParticipant,
  onInsertParticipant,
  onDeleteParticipant,
  onUpdateParticipantName,
  onNext,
}: AddParticipantsStepProps) => {
  const focusRef = useRef<HTMLInputElement>(null);

  // Handle focus changes when focusTargetId changes
  useEffect(() => {
    let timeoutRef: NodeJS.Timeout | undefined;

    if (focusParticipantId) {
      // Use setTimeout to ensure the DOM is updated
      timeoutRef = setTimeout(() => {
        focusRef.current?.focus();
      }, 0);
    }

    return () => {
      if (timeoutRef) {
        clearTimeout(timeoutRef);
      }
    };
  }, [focusParticipantId]);

  // Curried handlers to keep participant-specific data out of the JSX
  const handleNameChange = (id: string) => (e: ChangeEvent<HTMLInputElement>) => {
    onUpdateParticipantName(id, e.target.value);
  };

  // Curried handlers to keep participant-specific data out of the JSX
  const handleKeyDown = (id: string, index: number, name: string) => (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      onInsertParticipant(index + 1);
    } else if (e.key === 'Backspace' && name === '') {
      onDeleteParticipant(id);
    }
  };

  const handleDelete = (id: string) => () => {
    onDeleteParticipant(id);
  };

  const canDelete = participants.length > 3;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto">
        {/* Header */}
        <div className="space-y-2 pb-6">
          <h1 className="text-xl font-bold">Who is joining the secret santa?</h1>
          <div className="text-muted-foreground">Add the names of everyone joining the fun.</div>
        </div>

        {/* Participants List */}
        <Tooltip.Provider skipDelayDuration={0}>
          <div className="mb-4 space-y-2.5">
            {participants.map((p, index) => {
              const isDuplicate = duplicateParticipantIds.has(p.id);
              return (
                <InputGroup
                  key={p.id}
                  className="animate-in fade-in"
                  errorMessage={isDuplicate ? 'This name is duplicated.' : undefined}
                >
                  <InputGroupInput
                    id={p.id}
                    name={p.id}
                    placeholder={`Name ${index + 1}`}
                    type="text"
                    value={p.name}
                    onChange={handleNameChange(p.id)}
                    onKeyDown={handleKeyDown(p.id, index, p.name)}
                    ref={focusParticipantId === p.id ? focusRef : null}
                    autoFocus={index === 0}
                    aria-invalid={isDuplicate || undefined}
                  />
                  {canDelete && (
                    <InputGroupAddon align="inline-end">
                      <Tooltip.Root>
                        <Tooltip.Trigger asChild>
                          <InputGroupButton
                            size="icon-sm"
                            variant="ghost"
                            onClick={handleDelete(p.id)}
                            aria-label="Remove participant"
                          >
                            <XIcon className="size-4" />
                          </InputGroupButton>
                        </Tooltip.Trigger>
                        <Tooltip.Content>Remove participant</Tooltip.Content>
                      </Tooltip.Root>
                    </InputGroupAddon>
                  )}
                </InputGroup>
              );
            })}
          </div>
        </Tooltip.Provider>
      </div>

      {/* Add Participant */}
      <div className="mb-8 flex justify-end">
        <Button variant="secondary" size="sm" onClick={onAppendParticipant}>
          <PlusIcon className="mr-1 size-4" weight="bold" /> <span>Add name</span>
        </Button>
      </div>

      {error && (
        <Alert variant="error" className="mb-4">
          {error}
        </Alert>
      )}

      <Button className="w-full" onClick={onNext}>
        <span>Continue</span> <ArrowRightIcon className="ml-2 size-4" weight="bold" />
      </Button>
    </div>
  );
};
